import {
	fallbackToEmptyObject,
	type Issue,
	type IssueAssociation,
	type IssueType,
	type IssueTypeFieldValues,
	type IssueTypeFieldValuesAttachment,
	type IssueTypeIdentifier,
	type IssueTypeIdentifierValue,
	type Project,
	type Stored,
	submittedAtModelSortFn,
} from "@overmap-ai/common"
import {
	restructureSelectorWithArgs,
	selectIssueAssociations,
	selectIssues,
	selectIssueTypeFieldValues,
	selectIssueTypeFieldValuesAttachments,
	selectIssueTypeIdentifierValues,
} from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"

import type { RootSelectorWithArgs } from "../typings"

export const selectIssuesOfProjectAndIssueType = restructureSelectorWithArgs(
	createSelector(
		[
			selectIssues,
			(_, projectId: Project["uuid"]) => projectId,
			(_, __, issueTypeId: IssueType["uuid"]) => issueTypeId,
		],
		(issues, projectId, issueTypeId) => {
			return issues.filter(({ project, issue_type }) => project === projectId && issue_type === issueTypeId)
		},
	),
)

export const selectIssueAssociationsByIssue = createSelector([selectIssueAssociations], (issueAssociations) => {
	const ret: Record<Issue["uuid"], Stored<IssueAssociation>[] | undefined> = {}

	for (const association of issueAssociations) {
		const { issue } = association

		if (!ret[issue]) ret[issue] = []

		ret[issue]!.push(association)
	}

	return fallbackToEmptyObject(ret)
})

export const selectLatestIssueTypeFieldValuesByIssue = createSelector(
	[selectIssueTypeFieldValues],
	(issueTypeFieldValues) => {
		const ret: Record<Issue["uuid"], Stored<IssueTypeFieldValues> | undefined> = {}

		for (const fieldValues of issueTypeFieldValues) {
			const current = ret[fieldValues.issue]

			if (!current || current.submitted_at < fieldValues.submitted_at) {
				ret[fieldValues.issue] = fieldValues
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestIssueTypeFieldValuesOfIssue: RootSelectorWithArgs<
	Issue["uuid"],
	Stored<IssueTypeFieldValues> | undefined
> = restructureSelectorWithArgs(
	createSelector(
		[selectIssueTypeFieldValues, (_, issueId: Issue["uuid"]) => issueId],
		(issueTypeFieldValues, issueId) => {
			return issueTypeFieldValues
				.filter(({ issue }) => issue === issueId)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)

export const selectIssueTypeIdentifierValuesByIssue = createSelector(
	[selectIssueTypeIdentifierValues],
	(issueTypeIdentifierValues) => {
		const ret: Record<Issue["uuid"], Record<IssueTypeIdentifier["uuid"], Stored<IssueTypeIdentifierValue>>> = {}

		for (const identifierValue of issueTypeIdentifierValues) {
			const { issue, issue_type_identifier } = identifierValue

			if (!ret[issue]) ret[issue] = {}

			const current = ret[issue][issue_type_identifier]

			if (!current || current.submitted_at <= identifierValue.submitted_at) {
				ret[issue][issue_type_identifier] = identifierValue
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectAttachmentsByIssueTypeFieldValues = createSelector(
	[selectIssueTypeFieldValuesAttachments],
	(issueTypeFieldValuesAttachments) => {
		const ret: Record<IssueTypeFieldValues["uuid"], Stored<IssueTypeFieldValuesAttachment>[] | undefined> = {}

		for (const assetTypeFieldValuesAttachment of issueTypeFieldValuesAttachments) {
			const { field_values } = assetTypeFieldValuesAttachment
			if (!(field_values in ret)) ret[field_values] = []
			ret[field_values]!.push(assetTypeFieldValuesAttachment)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestIssueTypeIdentifierValueOfIssue = restructureSelectorWithArgs(
	createSelector(
		[
			selectIssueTypeIdentifierValues,
			(_, issueId: Issue["uuid"]) => issueId,
			(_, __: Issue["uuid"], issueTypeIdentifierId: IssueTypeIdentifier["uuid"]) => issueTypeIdentifierId,
		],
		(issueTypeIdentifierValues, issueId, issueTypeIdentifierId): Stored<IssueTypeIdentifierValue> | undefined => {
			return issueTypeIdentifierValues
				.filter(
					({ issue, issue_type_identifier }) =>
						issue === issueId && issue_type_identifier === issueTypeIdentifierId,
				)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)
